import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  Input,
  Textarea,
  Button,
  Checkbox,
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
  FormDescription,
  AlertDescription,
  AlertTitle,
} from '@predoc/cow';
import { Hospital, Info } from 'lucide-react';
import {
  ControllerRenderProps,
  UseFormReturn,
  useWatch,
} from 'react-hook-form';
import { CreateProfileFormData, ProfileFormFieldProps } from '../types';
import { PROFILE_ICON_COLORS } from '../constants';
import { Alert } from '@predoc/cow';
import { FacesheetSelector } from './FacesheetSelector';
import { useGetOrganizationFacesheets } from '../hooks/facesheetsAPI';
import { useGetOrganizationInfo } from '../hooks/organizationAPI';
import { getAvailableRequestActions } from '../utils/requestActions';
import { Permission, useHasPermission } from '@/utils/roles';

interface ProfileFormProps {
  form: UseFormReturn<CreateProfileFormData>;
  profileId?: string;
  profileAvailableMethods?: string[];
  profileFacesheets?: Array<{
    facesheetConfigId: string;
    organizationId: string;
    displayName: string;
    templateTypes: string[];
  }>;
}

export const ProfileFormFields = ({
  form,
  profileId,
  profileAvailableMethods,
  profileFacesheets,
}: ProfileFormProps) => {
  // Manager can only edit name fields; Owner (who has CREATE_PROFILES) has full access
  const { hasPermission: hasFullAccess } = useHasPermission(
    Permission.CREATE_PROFILES,
  );
  const restrictedEdit = !hasFullAccess;
  const [hasExpeditedFacesheets] = useWatch({
    control: form.control,
    name: ['hasExpeditedFacesheets'],
  });

  const { data: organizationFacesheets } = useGetOrganizationFacesheets({
    orgWideOnly: true,
  });
  const { data: organizationInfo } = useGetOrganizationInfo();
  const allFacesheets = organizationFacesheets?.facesheets ?? [];

  // In edit flow, profileFacesheets includes org-wide + profile-specific facesheets,
  // so it's used as the lookup source to correctly resolve selected IDs that may
  // belong to profile-specific facesheets (isOrgWide: false). In create flow,
  // profileFacesheets is undefined so we fall back to allFacesheets (org-wide only).
  const facesheetLookup = profileFacesheets ?? allFacesheets;

  // Get available request actions based on organization settings and profile's available methods
  const availableRequestActions = getAvailableRequestActions({
    orgInfo: organizationInfo,
    organizationFacesheets: allFacesheets,
    profileAvailableMethods,
    profileFacesheets,
  });

  // Check if expedite facesheets feature is enabled
  const expediteFacesheetsEnabled =
    organizationInfo?.enableFacesheetExpedited ?? false;
  const handleActionChange = (
    checked: boolean,
    actionName: string,
    field: ControllerRenderProps<
      CreateProfileFormData,
      'defaultRetrievalMethods'
    >,
  ) => {
    const currentActions = field.value || [];

    let updatedActions;
    if (checked) {
      updatedActions = currentActions.includes(actionName)
        ? currentActions
        : [...currentActions, actionName];
    } else {
      // Remove method name from array
      updatedActions = currentActions.filter(
        (methodName) => methodName !== actionName,
      );
    }

    field.onChange(updatedActions);
  };

  const handleExpediteCheckboxChange = (checked: boolean) => {
    form.setValue('hasExpeditedFacesheets', checked, { shouldDirty: true });
    if (!checked) {
      form.setValue('expeditedFacesheetConfigIds', [], { shouldDirty: true });
    }
  };

  const handleExpeditedFacesheetsChange = (values: string[]) => {
    form.setValue('expeditedFacesheetConfigIds', values, { shouldDirty: true });
    if (values.length === 0) {
      form.setValue('hasExpeditedFacesheets', false, { shouldDirty: true });
    }
  };

  return (
    <>
      <div className="mb-6 grid grid-cols-2 items-start gap-6">
        <FormField
          control={form.control}
          name="displayName"
          render={({ field }: ProfileFormFieldProps<'displayName'>) => {
            const length = field.value?.length || 0;
            const overLimit = length > 80;
            return (
              <FormItem>
                <FormLabel>Profile Name *</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    value={field.value ?? ''}
                    placeholder="Charlotte Site"
                  />
                </FormControl>
                <FormDescription
                  className={overLimit ? 'text-destructive' : ''}
                >
                  {length}/80 characters
                </FormDescription>
                <FormMessage />
              </FormItem>
            );
          }}
        />

        <FormField
          control={form.control}
          name="avatarType"
          render={({ field }: ProfileFormFieldProps<'avatarType'>) => (
            <FormItem>
              <FormLabel>Profile Icon *</FormLabel>
              <FormControl>
                <div className="flex gap-2">
                  {PROFILE_ICON_COLORS.map((color) => (
                    <Button
                      key={color.name}
                      type="button"
                      variant={
                        field.value === color.name ? 'secondary' : 'ghost'
                      }
                      size="icon"
                      className="rounded-full"
                      onClick={() => field.onChange(color.name)}
                      disabled={restrictedEdit}
                    >
                      <Hospital className="size-4" color={color.color} />
                    </Button>
                  ))}
                </div>
              </FormControl>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="description"
          render={({ field }: ProfileFormFieldProps<'description'>) => (
            <FormItem className="col-span-2">
              <FormLabel>Description</FormLabel>
              <FormControl>
                <Textarea
                  {...field}
                  value={field.value ?? ''}
                  placeholder="Give the profile added context"
                />
              </FormControl>
              <FormDescription>Give the profile added context</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      {restrictedEdit ? null : (
        <Accordion type="single" collapsible className="rounded-lg border">
          <AccordionItem value="advanced-settings" className="border-none">
            <AccordionTrigger className="[border-bottom-right-radius:0] [border-bottom-left-radius:0] border-b px-4 py-3 text-base font-semibold hover:no-underline">
              Advanced Settings
              <span className="ml-auto rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                Optional
              </span>
            </AccordionTrigger>
            <AccordionContent className="p-8">
              <Alert variant="accent" className="mb-4">
                <Info size={16} />
                <AlertTitle className="text-sm">
                  Only modify these settings if you are certain.
                </AlertTitle>
                <AlertDescription className="text-muted-foreground text-sm">
                  These options affect request settings which may impact
                  billing.
                </AlertDescription>
              </Alert>

              <div className="grid grid-cols-2 items-start gap-8">
                <FormField
                  control={form.control}
                  name="defaultRetrievalMethods"
                  render={({
                    field,
                  }: ProfileFormFieldProps<'defaultRetrievalMethods'>) => (
                    <FormItem>
                      <FormLabel>Default Request Actions</FormLabel>
                      <FormDescription>
                        These actions will be selected by default when users
                        initiate a new patient request.
                      </FormDescription>
                      <div className="space-y-4">
                        {availableRequestActions.map((action, actionIndex) => {
                          // Check for specific error on this action's checkbox
                          const actionName = action.name;
                          const fieldError =
                            form.formState.errors?.requestActionErrors?.[
                              actionIndex
                            ];
                          const hasError = !!fieldError;
                          const isEnabled = (field.value || []).includes(
                            actionName,
                          );

                          return (
                            <FormControl key={actionName}>
                              <div
                                className={hasError ? 'text-destructive' : ''}
                              >
                                <Checkbox
                                  checked={isEnabled}
                                  onCheckedChange={(checked: boolean) =>
                                    handleActionChange(
                                      checked,
                                      actionName,
                                      field,
                                    )
                                  }
                                  label={action.displayName}
                                  className={
                                    hasError
                                      ? 'data-[state=checked]:border-destructive data-[state=checked]:bg-destructive!'
                                      : ''
                                  }
                                />
                              </div>
                            </FormControl>
                          );
                        })}
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div>
                  <FormField
                    control={form.control}
                    name="defaultFacesheetConfigIds"
                    render={({
                      field,
                    }: ProfileFormFieldProps<'defaultFacesheetConfigIds'>) => (
                      <FormItem>
                        <FormLabel>Default Facesheets</FormLabel>
                        <FormControl>
                          <FacesheetSelector
                            profileId={profileId ?? ''}
                            values={facesheetLookup.filter((f) =>
                              (field.value ?? []).includes(f.facesheetConfigId),
                            )}
                            onChange={(facesheets) =>
                              field.onChange(
                                facesheets.map((f) => f.facesheetConfigId),
                              )
                            }
                          />
                        </FormControl>
                        <FormMessage />
                        <FormDescription>
                          These facesheets will be selected by default when you
                          initiate a request.
                        </FormDescription>
                      </FormItem>
                    )}
                  />

                  {/* Expedite Facesheets Section - only show if enabled by organization */}
                  {expediteFacesheetsEnabled && (
                    <div className="mt-6">
                      <div className="flex items-start space-x-3">
                        <FormField
                          control={form.control}
                          name="hasExpeditedFacesheets"
                          render={({
                            field,
                          }: ProfileFormFieldProps<'hasExpeditedFacesheets'>) => (
                            <FormItem>
                              <FormLabel>Expedite Facesheets</FormLabel>
                              <FormControl>
                                <Checkbox
                                  checked={field.value}
                                  onCheckedChange={handleExpediteCheckboxChange}
                                  label="Expedite Facesheets"
                                  description="Expedite facesheets for faster turnaround time."
                                />
                              </FormControl>
                            </FormItem>
                          )}
                        />
                      </div>

                      {/* Facesheets to Expedite MultiSelect - only show when checkbox is checked */}
                      {hasExpeditedFacesheets && (
                        <div className="mt-4">
                          <FormField
                            control={form.control}
                            name="expeditedFacesheetConfigIds"
                            render={({
                              field,
                            }: ProfileFormFieldProps<'expeditedFacesheetConfigIds'>) => (
                              <FormItem>
                                <FormLabel>Facesheets to Expedite *</FormLabel>
                                <FormControl>
                                  <FacesheetSelector
                                    profileId={profileId ?? ''}
                                    values={facesheetLookup.filter((f) =>
                                      (field.value ?? []).includes(
                                        f.facesheetConfigId,
                                      ),
                                    )}
                                    onChange={(facesheets) =>
                                      handleExpeditedFacesheetsChange(
                                        facesheets.map(
                                          (f) => f.facesheetConfigId,
                                        ),
                                      )
                                    }
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      )}
    </>
  );
};
